import { Anchor, Badge, Breadcrumbs, Button, Card, Group, Stack, Tabs, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconChartBar,
  IconEdit,
  IconHandClick,
  IconHistory,
  IconMail,
  IconMailOpened,
  IconPhoto,
  IconStar,
  IconTrash,
  IconUser,
  IconUserCheck,
  IconUserMinus,
  IconUserPlus,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import ImpersonateProfile from '../../components/Profiles/ImpersonateProfile';
import Spinner from '../../components/Spinner';
import formatDate from '../../constants/formattedDate';
import prettifiedLabel from '../../constants/labelPrettifier';
import { Page } from '../../constants/pageTitles';
import { getUserIdFromProfileId } from '../../helpers/profileId';
import useFetch from '../../helpers/useFetch';
import ProfilesDetailsAggregatedStatsPage from './ProfilesDetailsAggregatedStatsPage';
import ProfilesDetailsAssetsPage from './ProfilesDetailsAssetsPage';
import ProfilesDetailsEmailsPage from './ProfilesDetailsEmailsPage';
import ProfilesDetailsEventsPage from './ProfilesDetailsEventsPage';
import ProfilesDetailsMessageStatsPage from './ProfilesDetailsMessageStatsPage';
import ProfilesDetailsOverview, { marketingSubscription } from './ProfilesDetailsOverview';
import ProfilesDetailsProductInteractionsPage from './ProfilesDetailsProductInteractionsPage';
import ProfilesDetailsRecommendationsPage from './ProfilesDetailsRecommendationsPage';

const page = `${Page['profiles']}`;

const ProfilesDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();

  const [subscriptionToggled, setSubscriptionToggled] = useState(false);
  const [deletePerformed, setDeletePerformed] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [profile, setProfile] = useState();
  const [companies, setCompanies] = useState();
  const [segmentMemberships, setSegmentMemberships] = useState([]);
  const [loyaltyPoints, setLoyaltyPoints] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const { accountId, domainId, subpage, id } = useParams();
  const { domain } = useSelector((state) => state.domain);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/profiles/${encodeURIComponent(id)}`)
      .then(({ profile, segmentMemberships, loyaltyPoints }) => {
        setProfile({
          ...profile,
          timestamp: formatDate(profile.timestamp),
          webPushSubscribedAt: profile.webPushSubscribedAt || null,
          appPushSubscribedAt: profile.appPushSubscribedAt || null,
          appPushPlatforms: profile.appPushPlatforms || [],
        });
        setSegmentMemberships(segmentMemberships);
        setLoyaltyPoints(loyaltyPoints);
      })
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/profiles/${encodeURIComponent(id)}/companies`)
      .then(({ companies }) => {
        setCompanies(companies);
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId, id]);

  const toggleSubscription = (profile) => {
    setSubscriptionToggled(true);
    const subscriptionType = profile.subscribedAt ? 'unsubscribe' : 'subscribe';
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify({
        email: profile.email,
        phoneNumber: profile.phoneNumber,
      }),
    };
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/profiles/${subscriptionType}`, requestOptions).catch(
      (error) => {
        setUserError(error);
        setSubscriptionToggled(false);
      }
    );
  };

  const del = (cb) => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append('authorization', `Bearer ${domain.secretKey}`);
    const requestOptions = {
      method: 'DELETE',
      headers: myHeaders,
      body: JSON.stringify({}),
    };

    fetch(
      `/api/v0/profiles?${new URLSearchParams({ profileId: getUserIdFromProfileId(profile.profileId) })}`,
      requestOptions
    )
      .then(() => {
        setDeletePerformed(true);
        if (typeof cb === 'function') {
          cb();
        }
      })
      .catch(setUserError);
  };

  const onSelect = (subpath) => {
    return navigate(`/accounts/${accountId}/domains/${domainId}/profiles/${encodeURIComponent(id)}/${subpath}`);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!profile || companies === undefined) {
    return <Spinner />;
  }

  const subscriptionButton = domain.subscriptionManagement === 'internal' && !subscriptionToggled && (
    <Button
      variant="default"
      size="sm"
      leftSection={profile.subscribedAt ? <IconUserMinus size={16} /> : <IconUserCheck size={16} />}
      onClick={() => toggleSubscription(profile)}
    >
      {profile.subscribedAt ? 'Unsubscribe' : 'Subscribe'}
    </Button>
  );

  const marketing = marketingSubscription(profile);
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email || 'Unnamed profile';

  let title = `${page}`;
  if (profile && profile.firstName && profile.lastName) {
    title += ` | ${profile.firstName} ${profile.lastName}`;
  }
  title += ' | Details';

  return (
    <>
      <Metadata title={title} />

      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/profiles?page=1`}>{page}</Anchor>
        <span>Details</span>
      </Breadcrumbs>
      <Card withBorder mb="sm">
        <Card.Section inheritPadding py="md" withBorder>
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon size={38} radius="xl" variant="light">
                <IconUser size={20} />
              </ThemeIcon>
              <div>
                <Title order={4}>{name}</Title>
                <Group gap={6} mt={4}>
                  <Badge size="sm" variant="light" color={marketing.color} leftSection={<marketing.icon size={12} />}>
                    {marketing.label}
                  </Badge>
                  {profile.consent && (
                    <Badge size="sm" variant="default">
                      {prettifiedLabel(profile.consent)}
                    </Badge>
                  )}
                  <Badge
                    size="sm"
                    variant="default"
                    leftSection={profile.registeredAt ? <IconUserPlus size={12} /> : <IconUserMinus size={12} />}
                  >
                    {profile.registeredAt ? 'Registered' : 'Guest'}
                  </Badge>
                </Group>
              </div>
            </Group>
            <Stack gap={4} align="flex-end">
              <Group gap="sm">
                <Button
                  size="sm"
                  leftSection={<IconEdit size={16} />}
                  component="a"
                  href={`#/accounts/${accountId}/domains/${domainId}/profiles/${encodeURIComponent(id)}/edit`}
                >
                  Edit
                </Button>
                <ImpersonateProfile userId={getUserIdFromProfileId(id)} />
                {subscriptionButton}
                <Button
                  size="sm"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => setDeleteConfirmationOpen(true)}
                >
                  Delete
                </Button>
              </Group>
              {subscriptionToggled && (
                <Text size="xs" c="dimmed">
                  Profile subscription status has been updated.
                </Text>
              )}
              {deletePerformed && (
                <Text size="xs" c="dimmed">
                  Profile has been scheduled for deletion.
                </Text>
              )}
            </Stack>
          </Group>
        </Card.Section>
        <Card.Section inheritPadding py="md">
          {userError && (
            <div style={{ marginBottom: 'var(--mantine-spacing-sm)' }}>
              <ErrorMessage message={userError.message} />
            </div>
          )}
          <ProfilesDetailsOverview
            profile={profile}
            companies={companies}
            segmentMemberships={segmentMemberships}
            loyaltyPoints={loyaltyPoints}
            accountId={accountId}
            domainId={domainId}
            currency={domain.currency}
          />
        </Card.Section>
        <ConfirmationModal
          show={deleteConfirmationOpen}
          close={() => setDeleteConfirmationOpen(false)}
          confirm={(cb) => del(cb)}
          message="This action cannot be undone!"
        />
      </Card>

      <Tabs value={subpage} onChange={onSelect} m={0} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="events" leftSection={<IconHistory size={16} />}>
            Recent Events
          </Tabs.Tab>
          <Tabs.Tab
            value="productInteractions"
            leftSection={<IconHandClick size={16} />}
            data-tour="profileDetailsProductInteractionsContent"
          >
            Product Interactions
          </Tabs.Tab>
          <Tabs.Tab value="personalizedRecommendations" leftSection={<IconStar size={16} />}>
            Personalized Recommendations
          </Tabs.Tab>
          <Tabs.Tab value="messageStats" leftSection={<IconMailOpened size={16} />}>
            Message Statistics
          </Tabs.Tab>
          <Tabs.Tab
            value="aggregatedStats"
            leftSection={<IconChartBar size={16} />}
            data-tour="profileDetailsAggregatedStatsContent"
          >
            Aggregated Statistics
          </Tabs.Tab>
          <Tabs.Tab value="assets" leftSection={<IconPhoto size={16} />}>
            Assets
          </Tabs.Tab>
          <Tabs.Tab value="emails" leftSection={<IconMail size={16} />}>
            Emails
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="events">
          <Card withBorder>
            <ProfilesDetailsEventsPage trackingId={id} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="productInteractions">
          <Card withBorder>
            <ProfilesDetailsProductInteractionsPage profile={profile} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="personalizedRecommendations">
          <Card withBorder>
            <ProfilesDetailsRecommendationsPage profile={profile} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="messageStats">
          <Card withBorder>
            <ProfilesDetailsMessageStatsPage trackingId={id} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="aggregatedStats">
          <Card withBorder>
            <ProfilesDetailsAggregatedStatsPage trackingId={id} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="assets">
          <Card withBorder>
            <ProfilesDetailsAssetsPage profile={profile} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="emails">
          <Card withBorder>
            <ProfilesDetailsEmailsPage profile={profile} />
          </Card>
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

export default ProfilesDetailsPage;
